/** One-time "set your password" page for invited admins or admin-issued resets (public; token in the URL). */
import { Banner, Button, Input } from '@uatp/ui';
import logo from '@uatp/ui/logo.png';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { type ApiError, post } from '../api/client';

export function SetPasswordPage() {
  const [params, setParams] = useSearchParams();
  const [token] = useState(() => params.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Set your password · UAT Proofing';
    // Keep the one-time token out of the address bar and browser history once read.
    if (params.has('token')) {
      params.delete('token');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) return setError('Use at least 12 characters.');
    if (password !== confirm) return setError('The two passwords do not match.');
    setBusy(true);
    try {
      await post('/auth/password/setup', { token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="grid min-h-dvh place-items-center overflow-auto bg-bg p-8">
      <div className="flex w-full max-w-[400px] flex-col gap-4">
        <div className="flex items-center justify-center gap-2.5">
          <img src={logo} alt="" className="size-7 object-contain" />
          <span className="text-15 font-semibold">UAT Proofing System</span>
        </div>
        <div className="flex flex-col gap-5 rounded-lg border border-border bg-surface p-8 shadow-md">
          <div>
            <h1 className="text-h1">{done ? 'Password set' : 'Set your password'}</h1>
            <p className="mt-1.5 text-13 text-text-2">
              {done ? 'You can now sign in with your email and this password.' : 'Choose a password for the UAT Proofing console. The link works once.'}
            </p>
          </div>
          {!token && !done && <Banner variant="error">This link is incomplete. Open the link from your email again.</Banner>}
          {error && (
            <Banner variant="error" role="alert">
              {error}
            </Banner>
          )}
          {done ? (
            <Button asChild size="lg" fullWidth>
              <Link to="/login">Go to sign in</Link>
            </Button>
          ) : (
            <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
              <Input label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint="At least 12 characters." required />
              <Input label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              <Button type="submit" size="lg" loading={busy} disabled={!token || !password || !confirm} fullWidth>
                Set password
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
